import {compare} from 'natural-orderby'

import {emptyArray, emptyObject} from '../../../../../../../utils/empty'
import type {KeyValue} from '../../../../../../../utils/object'
import {objectValues} from '../../../../../../../utils/object'
import {AgentTreeItemType} from '../../../../AgentsSidebar/AgentsSidebar.types'
import {insertSorted, matchName} from '../../../AgentPoolPage.utils'

import type {SelectionState} from './AssignAgentsDialog.hooks'
import type {
  AgentsSelectionSummary,
  AssignableAgentPool,
  AssignableAgentsTree,
  AssignableItemsTreeBranch,
} from './AssignAgentsDialog.types'

const branchProps = {
  items: emptyArray,
  selectedCloudImagesCount: 0,
  selectedCloudAgentsCount: 0,
  selectedAgentsCount: 0,
}

const naturalCompare = compare()

interface Sortable {
  name: string
  type: AgentTreeItemType.CLOUD_IMAGE | AgentTreeItemType.AGENT
  environment: {
    os: {
      type: string
    } | null
  } | null
}

const typeScore = {
  [AgentTreeItemType.CLOUD_IMAGE]: 0,
  [AgentTreeItemType.AGENT]: 1,
}

function compareItemsByName<T extends Sortable>(
  {name: name1, type: type1}: T,
  {name: name2, type: type2}: T,
): number {
  if (type1 === type2) {
    return naturalCompare(name1, name2)
  }

  return typeScore[type1] - typeScore[type2]
}

export function buildAssignableAgentsTree(
  pool: AssignableAgentPool | null,
  cloudImagesSelection: SelectionState,
  agentsSelection: SelectionState,
): AssignableAgentsTree {
  let tree: KeyValue<string, AssignableItemsTreeBranch> = emptyObject
  pool?.assignableCloudImages?.edges.forEach(({node: {agentPool, ...node}}) => {
    if (agentPool?.name != null && node.agentTypeRawId != null) {
      const {id, rawId, name} = agentPool
      const branch = tree[rawId] || {id, rawId, name, ...branchProps}
      const selected = cloudImagesSelection[node.agentTypeRawId] === true
      const item = {agentPool, ...node, type: AgentTreeItemType.CLOUD_IMAGE, selected}
      tree = {
        ...tree,
        [rawId]: {
          ...branch,
          items: insertSorted(branch.items, item, compareItemsByName),
          selectedCloudImagesCount: branch.selectedCloudImagesCount + +selected,
          selectedCloudAgentsCount:
            branch.selectedCloudAgentsCount + (selected ? item.instances?.count || 0 : 0),
        },
      }
    }
  })
  pool?.assignableAgents?.edges.forEach(({node: {agentPool, ...node}}) => {
    let branch

    if (node.authorized === false) {
      branch = tree.unauthorized || {
        id: 'unauthorized',
        rawId: 'unauthorized',
        name: 'Unauthorized',
        ...branchProps,
      }
    } else if (agentPool?.name != null) {
      branch = tree[agentPool.rawId] || {
        id: agentPool.id,
        rawId: agentPool.rawId,
        name: agentPool.name,
        ...branchProps,
      }
    }

    if (branch != null) {
      const selected = agentsSelection[node.rawId] === true
      tree = {
        ...tree,
        [branch.rawId]: {
          ...branch,
          items: insertSorted(
            branch.items,
            {agentPool, ...node, type: 'agent', selected},
            compareItemsByName,
          ),
          selectedAgentsCount: branch.selectedAgentsCount + +selected,
        },
      }
    }
  })

  return objectValues(tree) as AssignableAgentsTree
}

export function matchAssignableAgentsTree(
  tree: AssignableAgentsTree,
  searchQuery: string,
): AssignableAgentsTree {
  return tree.reduce((matchingTree, node) => {
    const matching = searchQuery === '' || matchName(node.name, searchQuery)

    const items = node.items.reduce(
      (matchingItems, item) =>
        matching || matchName(item.name, searchQuery) ? [...matchingItems, item] : matchingItems,
      emptyArray,
    )

    return items.length > 0 ? [...matchingTree, {...node, items}] : matchingTree
  }, emptyArray)
}

export function getAgentsSelectionSummary(
  agentsTree: AssignableAgentsTree,
): AgentsSelectionSummary {
  let selectedCloudImagesCount = 0
  let selectedCloudAgentsCount = 0
  let selectedSelfHostedAgentsCount = 0
  let selectedUnauthorizedAgentsCount = 0

  for (let i = 0, l = agentsTree.length; i < l; i++) {
    const branch = agentsTree[i]
    if (branch.id === 'unauthorized') {
      selectedUnauthorizedAgentsCount = branch.selectedAgentsCount
    } else {
      selectedCloudImagesCount += branch.selectedCloudImagesCount
      selectedCloudAgentsCount += branch.selectedCloudAgentsCount
    }

    selectedSelfHostedAgentsCount += branch.selectedAgentsCount
  }

  return {
    selectedCloudImagesCount,
    selectedCloudAgentsCount,
    selectedSelfHostedAgentsCount,
    selectedUnauthorizedAgentsCount,
  }
}
